import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { FullScreenPreviewService } from 'src/app/utils/full-screen-preview/full-screen-preview.service';
import { SupportAttachment } from '../support-system.models';
import { isImageType } from './support-upload-queue';

/**
 * Opens chat files in the dashboard's full-screen preview (client widget and
 * team inbox): images as a gallery of the message's images, PDFs and videos
 * inline. Other types can't be previewed; callers open those in a new tab.
 */
@Injectable({ providedIn: 'root' })
export class SupportAttachmentPreviewService {
  private preview = inject(FullScreenPreviewService);

  /** The preview is showing (e.g. so Escape closes it, not the chat panel). */
  readonly isOpen = toSignal(this.preview.getIsPreviewOpen(), {
    initialValue: false,
  });

  canPreview(mimeType: string): boolean {
    return (
      isImageType(mimeType) ||
      mimeType === 'application/pdf' ||
      mimeType.startsWith('video/')
    );
  }

  /**
   * `attachments` are the message's files, `attachment` the clicked one.
   * `urlFor` gives a viewable link: a fresh signed URL, or the local preview
   * of a file that is still being sent.
   */
  open(
    attachments: SupportAttachment[],
    attachment: SupportAttachment,
    urlFor: (attachment: SupportAttachment) => Observable<string>,
  ): void {
    if (isImageType(attachment.mimeType)) {
      this.openImages(attachments.filter((a) => isImageType(a.mimeType)), attachment, urlFor);
      return;
    }
    urlFor(attachment).subscribe({
      next: (src) => {
        this.preview.setContent({
          src,
          html: '',
          label: attachment.name,
          isHTML: false,
          showLabel: false,
          isPdf: attachment.mimeType === 'application/pdf',
          isVideo: attachment.mimeType.startsWith('video/'),
        });
        this.preview.openPreview();
      },
      error: () => {},
    });
  }

  /** All images of the message, so the arrows move between them. */
  private openImages(
    images: SupportAttachment[],
    clicked: SupportAttachment,
    urlFor: (attachment: SupportAttachment) => Observable<string>,
  ): void {
    forkJoin(
      images.map((image) =>
        urlFor(image).pipe(
          map((src) => ({ src: cssSafeUrl(src), label: image.name, id: image.id })),
          // One broken link shouldn't keep the others from opening.
          catchError(() => of(null)),
        ),
      ),
    ).subscribe((resolved) => {
      const items = resolved.filter(
        (item): item is { src: string; label: string; id: string } => !!item,
      );
      const index = Math.max(0, items.findIndex((item) => item.id === clicked.id));
      if (!items.length) return;
      this.preview.setContent({
        src: items[index].src,
        html: '',
        label: items[index].label,
        isHTML: false,
        showLabel: true,
        items: items.map(({ src, label }) => ({ src, label })),
        index,
      });
      this.preview.openPreview();
    });
  }
}

/**
 * The preview draws images as an unquoted CSS `url(…)`, where `'`, `"`, `(`,
 * `)` and whitespace are invalid. Signed download links contain `'` (the
 * backend's `filename*=UTF-8''<name>`), so they are percent-encoded here;
 * storage decodes them back, so the signature still matches.
 */
function cssSafeUrl(url: string): string {
  return url.replace(
    /[\s'"()]/g,
    (char) => '%' + char.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0'),
  );
}
